/**
 * Create-application wizard. Two steps inside one dialog:
 *
 *   1. **Application** — org, name, description, template hint.
 *   2. **Repository** — repo URL, ref, pathInRepo. Creates the `auto-repo`
 *      Component immediately. Optional: an operator who doesn't have the
 *      repo yet (or doesn't *have* a repo, e.g. a "what to monitor" config
 *      for a customer's M365 tenant) can skip this step.
 *
 * The repository step is **automatically skipped** when the picked template
 * is a SaaS-class template (`capabilities.repoDiscovery === false`) — at
 * that point there's nothing to attach a repo to (Microsoft 365 isn't in
 * git). The wizard creates the app and dismisses.
 *
 * "Visible orgs" = the union of every org the user has a direct membership
 * row in, plus every descendant of those orgs. Same source as the Orgs
 * page and CreateOrgDialog use.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import {
  api,
  ApiError,
  type Application,
  type Component,
  type ComponentRole,
  type DiscoveryProposal,
} from '@/api/client.js';
import {
  DiscoveryPreview,
  defaultSelection,
  selectionKey,
  selectionsFromKeys,
} from '@/components/applications/discovery-preview.js';
import { resolveCapabilities } from '@/lib/templates.js';
import {
  AddComponentForm,
  ROLE_VISUALS,
} from '@/components/applications/CreateComponentDialog.js';
import { WizardChain } from '@/wizards/WizardChain.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void | Promise<void>;
  /**
   * Pre-seed the parent-org dropdown. Used by the wizard chain that
   * runs after creating a new org (CreateOrgDialog → here): the new
   * org is pre-selected so the operator doesn't have to find it
   * again in the dropdown.
   *
   * When unset, the dropdown defaults to the first visible org
   * (existing behaviour).
   */
  initialOrgId?: string;
}

/**
 * Template hints for v1. Hard-coded with the same ids the plugin manifests
 * declare. Will move to /api/v1/applications/templates once the plugin
 * registry exposes its templates via the API — that change is its own concern.
 *
 * Templates split into two flavours:
 *  - **Infrastructure** (Drupal / WordPress / etc.): a real codebase + server
 *    we'll SSH into. Discovery later auto-matches these from filesystem
 *    markers in the plugin-applications manifest.
 *  - **SaaS** (microsoft-365 / google-workspace): a customer tenant we
 *    surface telemetry on. Operator picks explicitly; no marker-based
 *    discovery. The wizard auto-skips the repo step for these.
 */
type TemplateOption = { value: string; label: string; description: string; group: 'infra' | 'saas' | 'none' };

const TEMPLATE_OPTIONS: TemplateOption[] = [
  { value: '', label: 'No template', description: 'Pick later when discovery runs.', group: 'none' },
  // Infrastructure (ship today)
  { value: 'drupal-10', label: 'Drupal 10', description: 'Drupal 10 site with the common subsystems.', group: 'infra' },
  { value: 'wordpress', label: 'WordPress', description: 'WordPress with wp-cli + uploads dir.', group: 'infra' },
  { value: 'laravel', label: 'Laravel', description: 'PHP / Laravel app + queue + DB.', group: 'infra' },
  { value: 'rails', label: 'Rails', description: 'Rails app + DB + worker.', group: 'infra' },
  { value: 'node-service', label: 'Node service', description: 'Generic Node service from package.json.', group: 'infra' },
  { value: 'static-site', label: 'Static site', description: 'Built assets, no server-side runtime.', group: 'infra' },
  // SaaS (scaffolding — see plugin-saas)
  {
    value: 'microsoft-365',
    label: 'Microsoft 365 tenant',
    description: "A customer's M365 tenant — Exchange, SharePoint, Teams. (scaffolding)",
    group: 'saas',
  },
  {
    value: 'google-workspace',
    label: 'Google Workspace tenant',
    description: "A customer's Workspace domain — Gmail, Drive, Meet. (scaffolding)",
    group: 'saas',
  },
];

/**
 * Wizard steps:
 *
 *   'app' — operator picks org / name / template.
 *   'add-component' — AddComponentForm rendered inline; the parent
 *     suggests an `initialRole` on each iteration. First iteration
 *     suggests 'repo' since that's the highest-discovery-payoff
 *     component to attach first; subsequent iterations walk the rest
 *     of DISCOVERY_PAYOFF_ORDER, skipping any role whose default slug
 *     is already taken on the app.
 *   'next-suggest' — between iterations, a small panel suggests the
 *     next role with two buttons: "Add this <role>" (back to
 *     add-component with that role) and "Done — go to application"
 *     (advances to app-chain).
 *   'app-chain' — fires the registered `app.created` wizard hooks
 *     before the final review. Today no hooks are registered against
 *     this event, so the chain controller's no-hook short-circuit
 *     fires onAllDone immediately and we advance. Future backup /
 *     monitoring / issue-tracker plugins drop in by registering
 *     against `app.created` (orders 20, 30, 40 per the
 *     wizards/types.ts convention).
 *   'final-review' — terminal step. Runs a fresh `/discover` against
 *     the new app, shows a summary of what was created in the wizard,
 *     and surfaces any proposals the operator can apply inline. When
 *     discovery returns no proposals we render just the summary +
 *     Done; when there are proposals the operator can apply selected
 *     before navigating away. Either path ends with finishWith(app).
 */
type Step = 'app' | 'add-component' | 'next-suggest' | 'app-chain' | 'final-review';

/**
 * Order in which we suggest Component roles to add after the app is
 * created. Front-loaded for discovery payoff — adding these in this
 * order unlocks the most cascading value:
 *
 *   1. repo      — .deployit/ → scripts, suggested Components, more
 *   2. web       — monitoring + analytics targets attach here
 *   3. database  — backups + restore key off this
 *   4. files     — completes the backup story for stateful apps
 *   5. cache     — runtime
 *   6. queue     — runtime
 *   7. worker    — runtime
 *   8. search    — runtime
 *   9. other     — catch-all; last because it's the least informative
 */
const DISCOVERY_PAYOFF_ORDER: ComponentRole[] = [
  'repo',
  'web',
  'database',
  'files',
  'cache',
  'queue',
  'worker',
  'search',
  'other',
];

/**
 * Pick the next role to suggest given what's already on the app.
 * Returns null when every role's default slug has been claimed —
 * the wizard finishes at that point.
 */
function nextSuggestedRole(takenSlugs: string[]): ComponentRole | null {
  for (const r of DISCOVERY_PAYOFF_ORDER) {
    const defaultSlug = ROLE_VISUALS[r].defaultSlug;
    if (!takenSlugs.includes(defaultSlug)) return r;
  }
  return null;
}

export function CreateApplicationDialog({
  open,
  onOpenChange,
  onCreated,
  initialOrgId,
}: Props): JSX.Element {
  const navigate = useNavigate();

  const orgsQuery = useQuery({
    queryKey: ['organisations'],
    queryFn: () => api.organisations.list(),
  });
  const orgs = orgsQuery.data ?? [];

  // Step 1 (app) state
  const [step, setStep] = useState<Step>('app');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [orgId, setOrgId] = useState('');

  // Component-loop state: the created app + every Component the
  // operator has added through the loop, plus a `key` we bump every
  // iteration so AddComponentForm starts fresh each time.
  const [createdApp, setCreatedApp] = useState<Application | null>(null);
  const [addedComponents, setAddedComponents] = useState<Component[]>([]);
  const [addComponentKey, setAddComponentKey] = useState(0);
  // The role the form is currently being asked to add. Drives the
  // form's initialRole + the "Add this <role>" button label.
  const [suggestedRole, setSuggestedRole] = useState<ComponentRole>('repo');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync `orgId` to the first visible org whenever the list arrives.
  // initialOrgId takes precedence if it's in the list — that's the
  // chain path from CreateOrgDialog, which pre-seeds the parent org
  // so the operator doesn't have to find their newly-created org in
  // the dropdown.
  useEffect(() => {
    if (orgs.length === 0) return;
    if (initialOrgId && orgs.some((o) => o.id === initialOrgId) && orgId !== initialOrgId) {
      setOrgId(initialOrgId);
      return;
    }
    if (!orgs.some((o) => o.id === orgId)) {
      setOrgId(orgs[0]!.id);
    }
  }, [orgs, orgId, initialOrgId]);

  function reset() {
    setStep('app');
    setName('');
    setDescription('');
    setTemplateId('');
    setOrgId(initialOrgId && orgs.some((o) => o.id === initialOrgId) ? initialOrgId : orgs[0]?.id ?? '');
    setCreatedApp(null);
    setAddedComponents([]);
    setAddComponentKey(0);
    setSuggestedRole('repo');
    setError(null);
    setSubmitting(false);
  }

  // Ref guard for the dismiss-confirm pattern below — `finishWith`
  // closes programmatically after a successful wizard completion,
  // and that path must NOT prompt "discard your changes?".
  const closingFromSuccess = useRef(false);

  /**
   * Final exit — refresh caller's list, dismiss the dialog, navigate
   * into the new app's detail page so the operator can keep working.
   */
  async function finishWith(app: Application) {
    closingFromSuccess.current = true;
    await onCreated();
    onOpenChange(false);
    reset();
    await navigate({
      to: '/p/applications/applications/$slug',
      params: { slug: app.slug },
      search: { org: app.orgSlug },
    });
  }

  /**
   * Step 1 submit. Create the application. Then either:
   *   - SaaS template (capabilities.repoDiscovery === false): nothing
   *     to attach, finishWith immediately.
   *   - Otherwise: enter the component loop with initialRole='repo'
   *     (the highest-discovery-payoff slot).
   */
  async function submitAppStep(e: FormEvent) {
    e.preventDefault();
    if (!orgId) {
      setError('Pick an organisation to own this application.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.applications.create(orgId, {
        name,
        ...(description ? { description } : {}),
        ...(templateId ? { templateId } : {}),
      });
      toast.success(`Created ${created.name}`);
      setCreatedApp(created);

      const caps = resolveCapabilities(templateId || null);
      if (!caps.repoDiscovery) {
        await finishWith(created);
        return;
      }

      setSuggestedRole('repo');
      setAddComponentKey((k) => k + 1);
      setStep('add-component');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create application.');
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Called by AddComponentForm when the operator finishes one
   * component. Track it, transition to next-suggest where the
   * operator decides whether to add another.
   */
  async function handleComponentCreated(component: Component) {
    setAddedComponents((prev) => [...prev, component]);
    setStep('next-suggest');
  }

  /**
   * Called by AddComponentForm when the operator hits Cancel. In our
   * loop context cancel === skip the rest of the loop, go to the app.
   */
  function handleComponentCancel() {
    if (createdApp) {
      void finishWith(createdApp);
    }
  }

  /**
   * "Add this <role>" — re-enter the form with the suggested role
   * pre-selected. Bumps the key so the form's internal state resets
   * (clears the previous component's name / URL / PAT / etc.).
   */
  function startNextComponent(role: ComponentRole) {
    setSuggestedRole(role);
    setAddComponentKey((k) => k + 1);
    setStep('add-component');
  }

  /**
   * Done button on the next-suggest panel. Instead of navigating
   * straight to the app, advance to the `app-chain` step so any
   * registered `app.created` wizard hooks fire first (backup,
   * monitoring, …). When zero hooks are registered the chain
   * controller fires onAllDone in a useEffect and we navigate
   * immediately — invisible to the operator.
   */
  function finishLoop() {
    if (createdApp) setStep('app-chain');
  }

  // takenSlugs threaded into AddComponentForm so it pre-validates
  // against everything we've already added in this wizard session.
  // Includes the role-default slug ("repo", "db", "web") so the same
  // role can't be picked twice via the picker — the operator can
  // still type a custom slug for a second of the same kind, but the
  // default-slug collision is the common case we guard.
  const takenSlugs = addedComponents.map((c) => c.slug);

  // Wizard dismiss guard. The dialog spans an app-create + multiple
  // component-create steps; an accidental click outside or stray
  // Escape press shouldn't blow away progress mid-chain. The X close
  // button still works but routes through the same confirm.
  //
  // Wording deliberately doesn't say "discard your changes" — by the
  // time the operator has reached the component-loop steps, the app
  // (and any components added) are already persisted server-side.
  // Closing just exits the wizard chain; nothing is rolled back.
  const handleOpenChange = (next: boolean): void => {
    if (!next && !closingFromSuccess.current) {
      const ok = window.confirm(
        createdApp
          ? `Close this wizard? ${createdApp.name} and any components you've added are already saved — you can keep adding from the application page.`
          : 'Close this wizard? Anything you typed will be lost.',
      );
      if (!ok) return;
    }
    closingFromSuccess.current = false;
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* max-w-xl (576px) instead of the default max-w-lg (512px) so
          the multi-step wizard has room for the app form, the
          AddComponentForm (inlined via ComponentLoopChrome), and the
          next-suggest step's role grid without crowding. twMerge
          dedupes the base `max-w-lg` cleanly. */}
      <DialogContent preventDismissOnInteractOutside className="max-w-xl">
        {step === 'app' && (
          <AppStep
            orgs={orgs}
            orgsLoading={orgsQuery.isLoading}
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            templateId={templateId}
            setTemplateId={setTemplateId}
            orgId={orgId}
            setOrgId={setOrgId}
            submitting={submitting}
            error={error}
            onCancel={() => handleOpenChange(false)}
            onSubmit={submitAppStep}
          />
        )}
        {step === 'add-component' && createdApp && (
          <ComponentLoopChrome
            appName={createdApp.name}
            addedCount={addedComponents.length}
          >
            <AddComponentForm
              key={addComponentKey}
              applicationId={createdApp.id}
              applicationName={createdApp.name}
              applicationOrgId={createdApp.orgId}
              applicationSlug={createdApp.slug}
              takenSlugs={takenSlugs}
              initialRole={suggestedRole}
              onCreated={handleComponentCreated}
              onCancel={handleComponentCancel}
            />
          </ComponentLoopChrome>
        )}
        {step === 'next-suggest' && createdApp && (
          <NextSuggestStep
            appName={createdApp.name}
            addedComponents={addedComponents}
            suggestedRole={nextSuggestedRole(takenSlugs)}
            onAdd={startNextComponent}
            onDone={finishLoop}
          />
        )}
        {step === 'app-chain' && createdApp && (
          <AppChainStep app={createdApp} onAllDone={() => setStep('final-review')} />
        )}
        {step === 'final-review' && createdApp && (
          <FinalReviewStep
            app={createdApp}
            addedComponents={addedComponents}
            onDone={() => void finishWith(createdApp)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Mount point for `app.created` wizard hooks. Today no hooks are
 * registered — the chain controller's no-hook short-circuit fires
 * onAllDone immediately and the operator sees the brief "Setting up
 * application…" placeholder before navigation kicks in. When future
 * plugins (backup at order 20, monitoring at 30, …) register their
 * hooks, this is the point in the flow where their setup steps run.
 */
function AppChainStep({
  app,
  onAllDone,
}: {
  app: Application;
  onAllDone: () => void;
}): JSX.Element {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Setting up {app.name}…</DialogTitle>
        <DialogDescription>
          Finishing follow-on setup steps before opening the application.
        </DialogDescription>
      </DialogHeader>
      <WizardChain
        eventKind="app.created"
        event={{ app }}
        isActive
        onAllDone={onAllDone}
      />
    </>
  );
}

/**
 * Terminal step: runs a fresh discovery pass, shows a summary of what
 * the wizard just created, and surfaces any `.deployit/` proposals
 * the operator can apply inline before navigating to the new app.
 *
 * Two paths:
 *   - Discovery returns proposals → render `DiscoveryPreview` with
 *     bulk-select, the operator picks + clicks "Apply selected".
 *   - Discovery returns nothing (no .deployit/, no drift, or no repo
 *     Component at all) → render just the summary + a single "Done"
 *     button. No nag.
 *
 * Either path ends with `onDone()` which the parent wires to
 * `finishWith(app)` — refreshes cache, dismisses dialog, navigates.
 *
 * Why we re-run discovery here even though the create-handler already
 * fired it: the auto-fire is fire-and-forget and may not have written
 * a pending row by the time the operator finishes the wizard chain.
 * A synchronous /discover on entry guarantees the proposal set is
 * current — and the operator's already in a "configuring this app"
 * mindset, so this is the natural moment to confront the proposals.
 */
function FinalReviewStep({
  app,
  addedComponents,
  onDone,
}: {
  app: Application;
  addedComponents: Component[];
  onDone: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const discoverQuery = useQuery({
    queryKey: ['discover', app.id, 'wizard-final-review'],
    queryFn: () => api.applications.discover(app.id),
    retry: false,
    staleTime: 0,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!discoverQuery.data) return;
    setSelected(defaultSelection(discoverQuery.data.proposals));
  }, [discoverQuery.data]);

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function selectByPredicate(pred: (p: DiscoveryProposal) => boolean): void {
    if (!discoverQuery.data) return;
    const next = new Set<string>();
    for (const p of discoverQuery.data.proposals) {
      if (p.kind === 'noop') continue;
      if (pred(p)) next.add(selectionKey(p));
    }
    setSelected(next);
  }

  const apply = useMutation({
    mutationFn: async () => {
      const selections = selectionsFromKeys(selected, discoverQuery.data?.proposals ?? []);
      if (selections.length === 0) return null;
      return api.applications.discoverApply(app.id, selections);
    },
    onSuccess: async () => {
      // Invalidate the same set the DiscoveryDialog does so the app
      // detail page reflects new rows + the bell/banner clears.
      await qc.invalidateQueries({ queryKey: ['scripts'] });
      await qc.invalidateQueries({ queryKey: ['script'] });
      await qc.invalidateQueries({ queryKey: ['applications'] });
      await qc.invalidateQueries({ queryKey: ['pendingDiscovery'] });
      await qc.invalidateQueries({ queryKey: ['notifications'] });
      onDone();
    },
  });

  // Discovery 422 ("no repo Component on this application") is the
  // expected case for SaaS apps and apps without a repo attached.
  // Render just the summary + Done — no error chrome.
  const isExpectedNoDiscoverySource =
    discoverQuery.error instanceof ApiError &&
    discoverQuery.error.status === 422 &&
    /repo component/i.test(discoverQuery.error.message);

  const hasProposals =
    discoverQuery.data &&
    (discoverQuery.data.totals.create > 0 ||
      discoverQuery.data.totals.update > 0 ||
      discoverQuery.data.totals.remove > 0);

  const selectedCount = discoverQuery.data
    ? selectionsFromKeys(selected, discoverQuery.data.proposals).length
    : 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          {app.name} is ready
        </DialogTitle>
        <DialogDescription>
          Here's everything that was set up in this wizard. If your repo's{' '}
          <span className="font-mono">.deployit/</span> directory has any
          proposals worth applying right away, they'll show below.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-2">
        {/* Summary of what the wizard created. Always renders. */}
        <CreatedSummary appName={app.name} components={addedComponents} />

        {/* Discovery sub-section. Three rendering paths handled inline. */}
        {discoverQuery.isFetching && !discoverQuery.data && (
          <div className="flex items-center justify-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Scanning the repo for proposals…
          </div>
        )}

        {discoverQuery.error && !isExpectedNoDiscoverySource && (
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            Couldn't run discovery now ({(discoverQuery.error as Error).message}). You can retry
            from the application page.
          </p>
        )}

        {discoverQuery.data && (
          <DiscoveryPreview
            report={discoverQuery.data}
            selected={selected}
            toggle={toggle}
            onSelectAll={() => selectByPredicate(() => true)}
            onSelectByKind={(k) => selectByPredicate((p) => p.kind === k)}
            onSelectNone={() => setSelected(new Set())}
            emptyStateMessage={
              <p className="rounded-md border border-border bg-muted/30 px-3 py-4 text-center text-sm text-muted-foreground">
                Nothing else to suggest — the application matches the repo's{' '}
                <span className="font-mono">.deployit/</span> already.
              </p>
            }
          />
        )}
      </div>

      <DialogFooter>
        {hasProposals ? (
          <>
            <Button variant="ghost" onClick={onDone} disabled={apply.isPending}>
              Skip for now
            </Button>
            <Button onClick={() => apply.mutate()} disabled={selectedCount === 0 || apply.isPending}>
              {apply.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {selectedCount > 0 ? `Apply ${selectedCount} and finish` : 'Apply selected'}
            </Button>
          </>
        ) : (
          <Button onClick={onDone}>Go to application</Button>
        )}
      </DialogFooter>
    </>
  );
}

/** Bullet-list of what the wizard created. */
function CreatedSummary({
  appName,
  components,
}: {
  appName: string;
  components: Component[];
}): JSX.Element {
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
      <p className="font-medium text-foreground">Created in this wizard</p>
      <ul className="ml-4 mt-1 list-disc space-y-0.5 text-xs">
        <li>
          Application <span className="font-medium">{appName}</span>
        </li>
        {components.length === 0 ? (
          <li className="text-muted-foreground">No components added yet</li>
        ) : (
          components.map((c) => (
            <li key={c.id}>
              <span className="font-mono">{c.slug}</span>{' '}
              <span className="text-muted-foreground">({c.role})</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/**
 * Small chrome around AddComponentForm when used inside the new-app
 * wizard. Surfaces the running progress ("App X — 2 components added
 * so far") above the form so the operator knows where they are in the
 * chain. Doesn't render footer chrome — AddComponentForm provides
 * its own DialogFooter.
 */
function ComponentLoopChrome({
  appName,
  addedCount,
  children,
}: {
  appName: string;
  addedCount: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
        <span>
          Building <span className="font-medium text-foreground">{appName}</span>
        </span>
        <span className="text-muted-foreground">
          {addedCount === 0 ? 'No components yet' : `${addedCount} added`}
        </span>
      </div>
      {children}
    </div>
  );
}

/**
 * Between AddComponentForm iterations. Shows what's been added,
 * suggests the next role from DISCOVERY_PAYOFF_ORDER, and lets the
 * operator pick "Add this <role>" or "Done — go to application".
 *
 * The operator can also pick a different role from a small "or pick
 * another" list — this stays cheap because the operator's already in
 * the wizard chain (no closing + re-opening the Components card).
 */
function NextSuggestStep({
  appName,
  addedComponents,
  suggestedRole,
  onAdd,
  onDone,
}: {
  appName: string;
  addedComponents: Component[];
  suggestedRole: ComponentRole | null;
  onAdd: (role: ComponentRole) => void;
  onDone: () => void;
}): JSX.Element {
  // Other roles still worth offering as a "or pick another" picker.
  // Skip the suggested one (operator can click the main CTA for it)
  // and anything whose default slug is taken. Cap at 4 so the panel
  // doesn't sprawl.
  const takenDefaultSlugs = new Set(addedComponents.map((c) => c.slug));
  const altRoles = DISCOVERY_PAYOFF_ORDER.filter(
    (r) => r !== suggestedRole && !takenDefaultSlugs.has(ROLE_VISUALS[r].defaultSlug),
  ).slice(0, 4);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Add another component?
        </div>
        <DialogTitle>What's next on {appName}?</DialogTitle>
        <DialogDescription>
          {addedComponents.length === 0
            ? "You haven't added anything yet. Pick a component to start with — the order below maximises what discovery can do for you."
            : `Added so far: ${addedComponents.map((c) => c.name).join(', ')}.`}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        {suggestedRole && (
          <SuggestedRolePanel role={suggestedRole} onAdd={() => onAdd(suggestedRole)} />
        )}

        {altRoles.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Or pick another
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {altRoles.map((r) => {
                const v = ROLE_VISUALS[r];
                const Icon = v.icon;
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={() => onAdd(r)}
                    className="flex flex-col items-start gap-1 rounded-md border border-border bg-background px-2.5 py-2 text-left text-xs hover:bg-accent/40"
                  >
                    <span className="flex items-center gap-1.5">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">{v.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button type="button" onClick={onDone}>
            Done — go to application
          </Button>
        </DialogFooter>
      </div>
    </>
  );
}

/**
 * Primary "add this next" CTA card on the next-suggest panel. Shows
 * the suggested role's icon, label, and discovery rationale so the
 * operator knows why we're recommending this one before the others.
 */
function SuggestedRolePanel({
  role,
  onAdd,
}: {
  role: ComponentRole;
  onAdd: () => void;
}): JSX.Element {
  const v = ROLE_VISUALS[role];
  const Icon = v.icon;
  const rationale = ROLE_RATIONALE[role] ?? v.hint;
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-3">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/15 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="space-y-0.5">
          <p className="text-sm font-medium">{v.label}</p>
          <p className="text-xs text-muted-foreground">{rationale}</p>
        </div>
      </div>
      <Button size="sm" onClick={onAdd}>
        Add {v.label}
      </Button>
    </div>
  );
}

/**
 * Why this role is the next-suggested one. Surfaced on the
 * NextSuggestStep panel so the operator sees the discovery value at
 * a glance rather than reading the generic role description.
 */
const ROLE_RATIONALE: Partial<Record<ComponentRole, string>> = {
  repo: 'Discovery reads .deployit/ from here — adds this first unlocks scripts, suggested components, and backup policies that the rest of the wizard would otherwise discover one at a time.',
  web: 'Monitoring (uptime, analytics) attaches to a web component. Adding this next lets the platform start watching the site as soon as it goes live.',
  database: 'Backups + restores key off database components. Adding this next unlocks the "Refresh dev from latest seed" workflow.',
  files: 'The other half of the backup story — uploads / asset directories. Pair with the database component for a complete restore.',
  cache: 'A runtime piece — operator scripts (clear-cache, warm-cache) can target it once declared.',
  queue: 'A runtime piece — exposes background-job operations.',
  worker: 'A long-running process. Pairs with queue for a complete async pipeline.',
  search: 'A runtime piece — index-rebuild scripts can target it.',
  other: 'For anything that doesn\'t fit the above. Free-form.',
};

/* ---------------------------------------------------------------------------
 * Step 1: application metadata
 * ------------------------------------------------------------------------- */

interface AppStepProps {
  orgs: Array<{ id: string; name: string; slug: string; path: string }>;
  orgsLoading: boolean;
  name: string;
  setName: (s: string) => void;
  description: string;
  setDescription: (s: string) => void;
  templateId: string;
  setTemplateId: (s: string) => void;
  orgId: string;
  setOrgId: (s: string) => void;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (e: FormEvent) => void;
}

function AppStep(props: AppStepProps): JSX.Element {
  const {
    orgs,
    orgsLoading,
    name,
    setName,
    description,
    setDescription,
    templateId,
    setTemplateId,
    orgId,
    setOrgId,
    submitting,
    error,
    onCancel,
    onSubmit,
  } = props;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Step 1 of 2
        </div>
        <DialogTitle>New application</DialogTitle>
        <DialogDescription>
          An application groups everything we'll manage for one system you host: environments,
          components, scripts, backups, monitors.
        </DialogDescription>
      </DialogHeader>

      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="org">Organisation</Label>
          <select
            id="org"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            disabled={orgsLoading || orgs.length === 0}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {indentForPath(o.path)}
                {o.name} /{o.slug}
              </option>
            ))}
          </select>
          {orgs.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Includes child orgs you administer via cascade — not just orgs you're a direct
              member of.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme marketing site"
          />
          <p className="text-xs text-muted-foreground">A slug is derived automatically from the name.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="desc">Description</Label>
          <Input
            id="desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="space-y-3">
          <Label>Template</Label>
          {(['none', 'infra', 'saas'] as const).map((group) => {
            const opts = TEMPLATE_OPTIONS.filter((o) => o.group === group);
            if (opts.length === 0) return null;
            const heading =
              group === 'none' ? null : group === 'infra' ? 'Infrastructure' : 'SaaS';
            return (
              <div key={group} className="space-y-1.5">
                {heading && (
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {heading}
                  </p>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {opts.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setTemplateId(opt.value)}
                      className={
                        'flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors ' +
                        (templateId === opt.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-background hover:bg-accent/40')
                      }
                    >
                      <span className="flex items-center gap-1.5 text-sm font-medium">
                        {opt.value === 'drupal-10' && <Sparkles className="h-3 w-3 text-primary" />}
                        {opt.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{opt.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">
            A hint for discovery. SaaS templates skip the repository step on the next screen
            (nothing to attach).
          </p>
        </div>

        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || !name || !orgId}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

/**
 * Indent an org option by depth from the materialised path so the tree
 * shape is visible in a flat <select>. Path format is `/parent/grand/etc`,
 * so depth = count of slash-separated segments. The root org has path `/`
 * which produces no indent. Same helper shape as CreateOrgDialog's;
 * worth lifting to `@/lib/orgs` if a third caller appears.
 */
function indentForPath(path: string): string {
  if (path === '/') return '';
  const depth = path.split('/').filter(Boolean).length;
  return '  '.repeat(depth) + '↳ ';
}
